import React, { useState } from 'react';
import {
  AddIngestorIncomingWebhookBody,
  IncomingWebhookAuthenticationType,
  UpdateIngestorIncomingWebhookBody,
} from '@loyalty/shared-common/ingestor-api-client';
import { Controller, useFormContext } from 'react-hook-form';
import { Feature } from '@loyalty/shared-common';
import { FormControl, MenuItem, TextField } from '@mui/material';
import { QlfFormRow } from '@qualifio/ui-commons';
import { useTranslation } from 'react-i18next';
import { useFeatures } from '../../../features/FeaturesContext';
import { useIncomingWebhookAuthType } from '../../../rules/rule-table-constants';
import { useSelectiveEffect } from '../../../shared/use-selective-effect';

export function IncomingWebhookAuthenticationForm() {
  const { t } = useTranslation();
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<AddIngestorIncomingWebhookBody | UpdateIngestorIncomingWebhookBody>();
  const [types, setTypes] = useState<IncomingWebhookAuthenticationType[]>([
    IncomingWebhookAuthenticationType.HUB_SIGNATURE,
  ]);
  const { features } = useFeatures();
  const { write: webhookWrite } = features[Feature.INCOMING_WEBHOOK];

  const webhookType = watch('type');
  const type = watch('authenticationConfig.type');

  useSelectiveEffect(
    () => {
      setTypes(Object.values(IncomingWebhookAuthenticationType));

      if (!type || !types.includes(type)) {
        setValue('authenticationConfig.type', types[0]);
      }
    },
    [setValue, types, type],
    [type],
  );

  const incomingWebhooksTypeTranslations = useIncomingWebhookAuthType();

  return (
    <>
      {types.length > 1 && (
        <Controller
          control={control}
          name="authenticationConfig.type"
          defaultValue={IncomingWebhookAuthenticationType.HUB_SIGNATURE}
          render={({ field: { ref, ...field }, fieldState: { error } }) => {
            return (
              <FormControl error={!!error}>
                <QlfFormRow label={t('incoming-webhooks.inputs.authentication.type')}>
                  <TextField
                    select
                    inputRef={ref}
                    {...field}
                    disabled={!webhookWrite}
                    helperText={error?.message}
                  >
                    {types.map(type => (
                      <MenuItem value={type} key={type}>
                        {incomingWebhooksTypeTranslations[type]}
                      </MenuItem>
                    ))}
                  </TextField>
                </QlfFormRow>
              </FormControl>
            );
          }}
        />
      )}
      {type === IncomingWebhookAuthenticationType.HUB_SIGNATURE && (
        <>
          <Controller
            control={control}
            name="authenticationConfig.secret"
            defaultValue=""
            render={({ field: { ref, ...field }, fieldState: { error } }) => {
              return (
                <QlfFormRow
                  label={t('incoming-webhooks.inputs.authentication.hub-signature.secret')}
                >
                  <TextField
                    {...field}
                    disabled={!webhookWrite}
                    inputRef={ref}
                    error={!!error}
                    helperText={error?.message}
                    type="password"
                    autoComplete="off"
                  />
                </QlfFormRow>
              );
            }}
          />
        </>
      )}

      {type === IncomingWebhookAuthenticationType.HEADER && (
        <>
          <Controller
            control={control}
            name="authenticationConfig.header"
            defaultValue="X-Authorization"
            render={({ field: { ref, ...field }, fieldState: { error } }) => {
              return (
                <QlfFormRow label={t('incoming-webhooks.inputs.authentication.header.header')}>
                  <TextField
                    {...field}
                    disabled={!webhookWrite}
                    inputRef={ref}
                    error={!!error}
                    helperText={error?.message}
                    autoComplete="off"
                  />
                </QlfFormRow>
              );
            }}
          />
          <Controller
            control={control}
            name="authenticationConfig.token"
            defaultValue=""
            render={({ field: { ref, ...field }, fieldState: { error } }) => {
              return (
                <QlfFormRow label={t('incoming-webhooks.inputs.authentication.header.token')}>
                  <TextField
                    {...field}
                    disabled={!webhookWrite}
                    inputRef={ref}
                    error={!!error}
                    helperText={error?.message}
                    type="password"
                    autoComplete="off"
                  />
                </QlfFormRow>
              );
            }}
          />
        </>
      )}

      {type === IncomingWebhookAuthenticationType.QUERY_PARAM && (
        <>
          <Controller
            control={control}
            name="authenticationConfig.key"
            defaultValue="token"
            render={({ field: { ref, ...field }, fieldState: { error } }) => {
              return (
                <QlfFormRow label={t('incoming-webhooks.inputs.authentication.query-param.key')}>
                  <TextField
                    {...field}
                    disabled={!webhookWrite}
                    inputRef={ref}
                    error={!!error}
                    helperText={error?.message}
                    autoComplete="off"
                  />
                </QlfFormRow>
              );
            }}
          />
          <Controller
            control={control}
            name="authenticationConfig.token"
            defaultValue=""
            render={({ field: { ref, ...field }, fieldState: { error } }) => {
              return (
                <QlfFormRow label={t('incoming-webhooks.inputs.authentication.query-param.token')}>
                  <TextField
                    {...field}
                    disabled={!webhookWrite}
                    inputRef={ref}
                    error={!!error}
                    helperText={error?.message}
                    type="password"
                    autoComplete="off"
                  />
                </QlfFormRow>
              );
            }}
          />
        </>
      )}
    </>
  );
}
